import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const applyAuth = (data) => {
    if (data?.token) localStorage.setItem("tb_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    return applyAuth(data);
  };

  const register = async (payload) => {
    const ref = localStorage.getItem("tb_ref") || "";
    const cref = localStorage.getItem("tb_cref") || "";
    const { data } = await api.post("/auth/register", { ...payload, ref, cref });
    if (ref) localStorage.removeItem("tb_ref");
    if (cref) localStorage.removeItem("tb_cref");
    applyAuth(data);
    return data;
  };

  const verifyOtp = async (channel, code) => (await api.post("/auth/verify-otp", { channel, code })).data;
  const resendOtp = async (channel) => (await api.post("/auth/resend-otp", { channel })).data;
  const loginOtpRequest = async (identifier) => (await api.post("/auth/login-otp/request", { identifier })).data;
  const loginOtpVerify = async (identifier, code) => applyAuth((await api.post("/auth/login-otp/verify", { identifier, code })).data);

  const exchangeGoogle = async (session_id) => {
    const ref = localStorage.getItem("tb_ref") || "";
    const cref = localStorage.getItem("tb_cref") || "";
    const { data } = await api.post("/auth/session", { session_id, ref, cref });
    if (ref) localStorage.removeItem("tb_ref");
    if (cref) localStorage.removeItem("tb_cref");
    return applyAuth(data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("tb_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyOtp, resendOtp, loginOtpRequest, loginOtpVerify, exchangeGoogle, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function homeForRole(role) {
  if (["admin", "super_admin", "responsable_logistique", "responsable_installation", "commercial", "comptable"].includes(role)) return "/admin";
  if (role === "professional") return "/pro";
  if (role === "livreur") return "/livreur";
  if (role === "influencer") return "/influenceur";
  return "/";
}
